<script lang="ts">
    import type {Tab, TabGroup, TabId} from '../types';
    import {exists} from 'shared/utils/custom-utils';
    import {getTabPagesMenuContext} from 'src/features/erp/components/erp-tab-pages';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import {slide} from 'svelte/transition';
    import {clickOutside} from 'shared/svelte-actions';
    import {getInjector} from 'src/core';
    import {KpPopoverPanel} from 'shared/ui-widgets/popover';
    import {tabPagesMenuStorage} from './tab-pages-menu.storage';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import {ErpFondPermissionsContextService, type FondPermissions} from 'src/features/erp';

    export let tabGroup: TabGroup;

    const singularButton = !exists(tabGroup.subtabs) || tabGroup.subtabs.length === 0;
    const {menuId, selectedId$, selectTab} = getTabPagesMenuContext();
    const {fondPermissions$} = getInjector().getByClass(ErpFondPermissionsContextService);
    const [floatingRef, floatingContent] = createFloatingActions(floatingUiCommonSettings('bottom-start', 0));

    let hoveringOver = false;
    let hoveringOverTimeout: number | null = null;
    let clickedOpen = false;
    let lastSelectedTabId = tabPagesMenuStorage.getLastSelectedTabId(menuId, tabGroup.id);
    $: highlightLastSelectedTabIfWithinGroup($selectedId$);

    const handleMouseEnter = () => {
        if (exists(hoveringOverTimeout)) {
            window.clearTimeout(hoveringOverTimeout);
        }
        hoveringOver = true;
    };

    const handleMouseLeave = () => {
        hoveringOverTimeout = window.setTimeout(() => {
            hoveringOver = false;
            hoveringOverTimeout = null;
        }, 250);
    };

    const handleSelectTab = (tabId: TabId) => {
        selectTab(tabId);
        lastSelectedTabId = tabId;
        tabPagesMenuStorage.saveLastSelectedTabId(menuId, tabGroup.id, tabId);

        // close popover
        clickedOpen = false;
        hoveringOver = false;
    };

    function getLastSelectedTab(tabId: TabId): Tab | null {
        if (!exists(tabId)) return null;
        return tabGroup.subtabs.find((subtab) => subtab.id === tabId);
    }

    function highlightLastSelectedTabIfWithinGroup(selectedTabId: TabId) {
        if (exists(selectedTabId) && (tabGroup.subtabs ?? []).some((subtab) => subtab.id === selectedTabId)) {
            lastSelectedTabId = selectedTabId;
        }
    }

    function filterSubtabsByPermissions(permissions: FondPermissions, subtabs: Tab[]): Tab[] {
        return subtabs.filter((subtab) => {
            if (!exists(subtab.permissionFondId)) return true;
            return permissions.editable.includes(subtab.permissionFondId);
        });
    }
</script>

{#if singularButton}
    <button class="tab-button"
            class:selected={$selectedId$ === tabGroup.id}
            on:click={() => selectTab(tabGroup.id)}>

        <IconedContent icon={tabGroup.icon}>
            {tabGroup.label}
        </IconedContent>
    </button>
{:else}
    {@const lastSelectedTab = getLastSelectedTab(lastSelectedTabId)}
    {@const filteredSubtabs = filterSubtabsByPermissions($fondPermissions$, tabGroup.subtabs)}

    <div class="multi-tab-button-container"
         class:has-last-selected-tab={exists(lastSelectedTab)}
         use:clickOutside
         role="button"
         tabindex="0"
         use:floatingRef
         on:click-outside={() => clickedOpen = false}
         on:mouseenter={handleMouseEnter}
         on:mouseleave={handleMouseLeave}>

        <button class="tab-button dropdown-button"
                on:click={() => clickedOpen = !clickedOpen}
                on:mouseenter={handleMouseEnter}>

            <IconedContent icon={tabGroup.icon} trailingIcon="angle-small-down">
                {tabGroup.label}
            </IconedContent>
        </button>

        {#if exists(lastSelectedTab)}
            <button class="tab-button last-option-button"
                    class:selected={$selectedId$ === lastSelectedTab.id}
                    on:mouseenter={() => hoveringOver = false}
                    on:click={() => selectTab(lastSelectedTab.id)}
                    in:slide={{axis: 'x', duration: 250}}>

                {lastSelectedTab.label}
            </button>
        {/if}

        <KpPopoverPanel additionalClasses="multi-tab-button-popover-panel"
                        open={hoveringOver || clickedOpen}
                        popoverMode="manual"
                        use={[floatingContent]}>

            <div class="subtabs-container">
                {#each filteredSubtabs as subtab}
                    <button class="subtab-button"
                            class:selected={$selectedId$ === subtab.id}
                            on:click={() => handleSelectTab(subtab.id)}>

                        <span class="button-label">{subtab.label}</span>

                        <span class="icon-container">
                            <UIcon icon={subtab.icon}/>
                        </span>
                    </button>
                {/each}
            </div>
        </KpPopoverPanel>
    </div>
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .multi-tab-button-container {
        display: flex;
        align-items: center;

        &.has-last-selected-tab {
            .dropdown-button {
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            }

            .last-option-button {
                border-left: none;
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
            }
        }

        .subtabs-container {
            display: flex;
            flex-direction: column;
            gap: @spacing-xs;
            min-width: 200px;
            max-height: 400px;
            overflow-y: auto;

            .subtab-button {
                display: flex;
                align-items: center;
                gap: @spacing-m;
                padding: @spacing-xs @spacing-s;
                font-weight: normal;
                border-radius: @border-radius-default;
                color: var(--text-default);
                white-space: nowrap;
                outline: none;
                border: none;
                background-color: transparent;
                text-decoration: none;
                cursor: pointer;

                &.selected {
                    background-color: @themed-body-bg-blue-highlighted;
                }

                .button-label {
                    flex: 1;
                    text-align: start;
                    font-size: @font-size-sm;
                }

                .icon-container {
                    color: @themed-text-muted;
                    font-size: @font-size-small;
                }

                &:not(.selected):hover {
                    background-color: @themed-panel-bg;
                }
            }
        }
    }

    .tab-button {
        padding: @spacing-s @spacing-m;
        background-color: @themed-body-bg;
        border: 1px solid @themed-border-default;
        color: @themed-text-default;
        white-space: nowrap;
        outline: none;
        cursor: pointer;
        border-radius: @border-radius-default;
        transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, opacity 0.2s ease-in-out, color 0.2s ease-in-out;

        &.selected {
            background-color: var(--selected-tab-highlight-color);
            border: 1px solid rgba(0, 0, 0, 0.2);
            color: white;
        }

        &:not(.selected) {
            &:hover {
                background-color: @themed-panel-bg;
            }
        }
    }

    :global {
        .multi-tab-button-popover-panel {
            margin-top: @spacing-sm;
            --popover-content-padding: @spacing-s;
        }
    }
</style>